import { useEffect } from "react";
import {
  ArrowRight,
  Zap,
  MapPin,
  Phone,
  Trees,
  Leaf,
  Wrench,
  Recycle,
  ShieldCheck,
  Sparkles,
  Bed,
  Maximize2,
  Layers,
  Building2,
  Users,
  HeartHandshake,
  Briefcase,
  Tent,
  Hammer,
  Truck,
  Home as HomeIcon,
  Settings2,
  Droplets,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ContactForm from "@/components/ContactForm";
import ProductSlideshow from "@/components/ProductSlideshow";

const ASSETS = {
  hero:
    "https://customer-assets.emergentagent.com/job_twin-rapide/artifacts/okvgnniy_ChatGPT%20Image%2015%20juin%202026%20a%CC%80%2014_59_28.png",
  twinInterior:
    "https://customer-assets.emergentagent.com/job_twin-rapide/artifacts/ukbb0iq8_1.png",
  twinInterior2:
    "https://customer-assets.emergentagent.com/job_twin-rapide/artifacts/l065uzc0_6n3ymkh3_2.png",
  village:
    "https://customer-assets.emergentagent.com/job_twin-rapide/artifacts/0xjgiki9_2.png",
  exterior:
    "https://customer-assets.emergentagent.com/job_twin-rapide/artifacts/gcbfnum6_3.png",
  exteriorDark:
    "https://customer-assets.emergentagent.com/job_twin-rapide/artifacts/hijy2fg2_2zc3r290_WhatsApp%20Image%202026-06-01%20at%2018.19.18.jpeg",
  plan:
    "https://customer-assets.emergentagent.com/job_fc40a26d-7ee2-4b99-ac23-dd0e9435603a/artifacts/3vp2dm01_3fh0xz63_YONO%20Twin.png",
  atelier:
    "https://customer-assets.emergentagent.com/job_fc40a26d-7ee2-4b99-ac23-dd0e9435603a/artifacts/jrye790e_fabrication-atelier.jpg",
};

const HERO_POINTS = [
  { icon: Zap, label: "Installation rapide" },
  { icon: Trees, label: "Fabrication française" },
  { icon: Leaf, label: "Logement bois durable" },
  { icon: Bed, label: "Deux chambres" },
  { icon: Briefcase, label: "Usages temporaires" },
  { icon: Settings2, label: "Options sur mesure" },
];

const CARACTERISTIQUES = [
  "Logement bois préfabriqué",
  "Deux chambres indépendantes",
  "Salle d'eau centrale (selon configuration)",
  "Version sans salle d'eau possible",
  "Coin kitchenette ou espace rangement",
  "Isolation adaptée à un usage confortable",
  "Structure bois robuste",
  "Transport et installation simplifiés",
  "Fabrication près de Nantes",
  "Finition intérieure chaleureuse en bois apparent",
];

const USAGES = [
  {
    icon: HeartHandshake,
    eyebrow: "01",
    title: "Hébergement d'urgence",
    text:
      "Pour les associations, collectivités et structures sociales ayant besoin de créer rapidement des places d'hébergement dignes, confortables et réutilisables.",
  },
  {
    icon: Tent,
    eyebrow: "02",
    title: "Logement saisonnier",
    text:
      "Pour les campings, domaines touristiques, exploitations agricoles, restaurants, hôtels ou sites de loisirs qui doivent héberger leur personnel pendant la saison.",
  },
  {
    icon: Users,
    eyebrow: "03",
    title: "Accueil temporaire de salariés",
    text:
      "Pour les entreprises confrontées à des difficultés de logement pour leurs équipes, intérimaires, renforts ou salariés en mobilité.",
  },
  {
    icon: Droplets,
    eyebrow: "04",
    title: "Sites avec sanitaires communs",
    text:
      "Le YONO TWIN peut être proposé dans une version simplifiée, sans salle d'eau intégrée, lorsque le site dispose déjà de blocs sanitaires ou d'équipements collectifs.",
  },
];

const AVANTAGES = [
  {
    n: "01",
    title: "Rapide à déployer",
    text: "Logement préfabriqué en atelier, limitant les travaux sur site.",
  },
  {
    n: "02",
    title: "Confortable",
    text: "Espaces nuit séparés, isolation soignée, ambiance bois chaleureuse.",
  },
  {
    n: "03",
    title: "Durable",
    text: "Construction bois, matériaux biosourcés, fabrication française.",
  },
  {
    n: "04",
    title: "Adaptable",
    text: "Options possibles selon le niveau d'équipement souhaité.",
  },
  {
    n: "05",
    title: "Réutilisable",
    text: "Solution déplaçable et réemployable selon l'évolution des besoins.",
  },
  {
    n: "06",
    title: "Valorisant",
    text: "Une image plus qualitative qu'un hébergement provisoire classique.",
  },
];

const FABRICATION = [
  { icon: Trees, label: "Fabrication française" },
  { icon: Layers, label: "Bois massif" },
  { icon: Leaf, label: "Matériaux biosourcés" },
  { icon: Wrench, label: "Préfabrication atelier" },
  { icon: ShieldCheck, label: "Contrôle qualité" },
  { icon: Recycle, label: "Approche durable" },
];

export default function TwinPage() {
  useEffect(() => {
    document.title =
      "Logement d'urgence et saisonnier bois | YONO TWIN";
  }, []);

  return (
    <div id="top" className="bg-[var(--yono-off-white)] flex-1 flex flex-col">
      <Header />

      {/* ========== HERO ========== */}
      <section
        className="relative pt-[140px] lg:pt-[150px]"
        data-testid="hero-section"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 lg:min-h-[88vh]">
          {/* Left: copy */}
          <div className="lg:col-span-5 flex items-center bg-[var(--yono-off-white)]">
            <div className="container-yono w-full py-16 lg:py-0">
              <div className="fade-up">
                <div className="eyebrow mb-6" data-testid="hero-eyebrow">
                  YONO TWIN · Hébergement
                </div>
                <h1
                  className="font-heading text-[34px] sm:text-[42px] lg:text-[52px] xl:text-[58px] leading-[1.05] tracking-tight text-[var(--yono-soft-black)]"
                  data-testid="hero-title"
                >
                  Une solution<br />
                  d'hébergement<br />
                  rapide, digne<br />
                  et durable.
                </h1>
                <p
                  className="mt-8 text-[16px] leading-[1.75] text-[var(--yono-warm-grey)] max-w-lg"
                  data-testid="hero-subtitle"
                >
                  YONO TWIN est un logement bois prêt à installer, conçu pour
                  l'hébergement d'urgence, le logement saisonnier et l'accueil
                  temporaire de personnes ou de salariés.
                </p>
                <p className="mt-5 text-[14px] leading-[1.8] text-[var(--yono-warm-grey)]/90 max-w-lg">
                  Compact, robuste, fabriqué en France, il accueille deux
                  personnes dans des espaces indépendants pensés pour les
                  associations, les entreprises, les collectivités et les
                  exploitants saisonniers.
                </p>

                <div className="mt-10 flex flex-wrap gap-3">
                  <a
                    href="#contact"
                    className="btn-primary"
                    data-testid="hero-cta-primary"
                  >
                    Demander une étude
                    <ArrowRight size={14} />
                  </a>
                  <a
                    href="#showroom"
                    className="btn-outline"
                    data-testid="hero-cta-secondary"
                  >
                    Visiter le showroom
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right: visual */}
          <div
            className="lg:col-span-7 relative bg-[var(--yono-beige)] min-h-[420px] lg:min-h-0 overflow-hidden"
            data-testid="hero-visual"
          >
            <img
              src={ASSETS.hero}
              alt="Logements YONO TWIN installés dans un paysage naturel au coucher du soleil"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Hero pictos band */}
        <div
          className="border-t border-b border-[var(--yono-border)] bg-[var(--yono-off-white)]"
          data-testid="hero-points"
        >
          <div className="container-yono py-6">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-y-5">
              {HERO_POINTS.map((p) => (
                <div
                  key={p.label}
                  className="flex items-center gap-3"
                  data-testid={`hero-point-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <p.icon
                    size={18}
                    className="text-[var(--yono-wood)] shrink-0"
                    strokeWidth={1.4}
                  />
                  <span className="text-[12px] tracking-[0.08em] uppercase text-[var(--yono-soft-black)]/85 font-medium">
                    {p.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========== BESOIN / PROBLÈME ========== */}
      <section className="section" data-testid="besoin-section">
        <div className="container-yono">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-5">
              <div className="eyebrow mb-6">Le besoin</div>
              <h2 className="font-heading text-[30px] sm:text-[36px] lg:text-[44px] leading-[1.1] tracking-tight">
                Répondre vite à un besoin d'hébergement concret.
              </h2>
            </div>
            <div className="lg:col-span-7 text-[15px] leading-[1.85] text-[var(--yono-warm-grey)] space-y-5">
              <p>
                Associations, collectivités, entreprises ou exploitants
                saisonniers doivent régulièrement faire face à des besoins
                d'hébergement urgents ou temporaires&nbsp;: accueil de personnes
                sans solution, logement de travailleurs saisonniers, renforts
                d'activité, personnel touristique, agricole ou événementiel.
              </p>
              <p>
                Les solutions disponibles sont souvent longues à mettre en
                place, peu confortables ou difficiles à adapter. Le YONO TWIN
                apporte une réponse simple&nbsp;: un logement bois compact,
                préfabriqué, transportable et prêt à installer.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== PRODUIT YONO TWIN ========== */}
      <section
        id="produit"
        className="section bg-[var(--yono-beige)]"
        data-testid="produit-section"
      >
        <div className="container-yono">
          <div className="eyebrow mb-6 text-[var(--yono-wood)]">
            Le logement de référence
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
            <h2 className="lg:col-span-7 font-heading text-[30px] sm:text-[36px] lg:text-[48px] leading-[1.1] tracking-tight">
              YONO TWIN — un logement compact pour deux personnes.
            </h2>
            <p className="lg:col-span-5 text-[15px] leading-[1.85] text-[var(--yono-warm-grey)] lg:pt-2">
              Conçu pour héberger deux personnes dans de bonnes conditions, le
              YONO TWIN propose deux espaces nuit distincts permettant de loger
              des salariés saisonniers, du personnel temporaire ou des
              personnes accompagnées par une structure sociale, tout en
              préservant un minimum d'intimité. Un seul logement de référence,
              décliné selon les usages.
            </p>
          </div>

          {/* Visuals + caracs */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
            <div className="lg:col-span-7">
              <ProductSlideshow
                slides={[
                  {
                    src: ASSETS.twinInterior,
                    alt: "Vue intérieure du logement YONO TWIN — deux chambres, kitchenette et salle d'eau",
                    fit: "cover",
                  },
                  {
                    src: ASSETS.twinInterior2,
                    alt: "Intérieur YONO TWIN — coupe détaillée avec kitchenette, banquette et salle d'eau",
                    fit: "cover",
                  },
                  {
                    src: ASSETS.village,
                    alt: "Logements YONO TWIN installés en village saisonnier au coucher du soleil",
                    fit: "cover",
                  },
                  {
                    src: ASSETS.exterior,
                    alt: "Logement YONO TWIN — vue extérieure dans un environnement paysager",
                    fit: "cover",
                  },
                  {
                    src: ASSETS.exteriorDark,
                    alt: "Logement YONO TWIN — façade bois noir en pleine nature au crépuscule",
                    fit: "cover",
                  },
                  {
                    src: ASSETS.plan,
                    alt: "Plan vu de dessus du YONO TWIN — deux chambres, salle d'eau, kitchenette",
                    fit: "contain",
                  },
                ]}
              />
            </div>

            <div className="lg:col-span-5">
              <div className="bg-white border border-[var(--yono-border)] p-8 lg:p-10 h-full">
                <div className="flex flex-wrap gap-x-7 gap-y-4 mb-8 pb-8 border-b border-[var(--yono-border)]">
                  <div className="flex items-center gap-2">
                    <Bed size={16} className="text-[var(--yono-wood)]" strokeWidth={1.4} />
                    <span className="text-[12px] tracking-[0.12em] uppercase text-[var(--yono-warm-grey)]">2 chambres</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Maximize2 size={16} className="text-[var(--yono-wood)]" strokeWidth={1.4} />
                    <span className="text-[12px] tracking-[0.12em] uppercase text-[var(--yono-warm-grey)]">Compact</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Layers size={16} className="text-[var(--yono-wood)]" strokeWidth={1.4} />
                    <span className="text-[12px] tracking-[0.12em] uppercase text-[var(--yono-warm-grey)]">Bois massif</span>
                  </div>
                </div>

                <div className="eyebrow text-[var(--yono-wood)] mb-5">
                  Caractéristiques
                </div>
                <ul className="space-y-3">
                  {CARACTERISTIQUES.map((c, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-3 text-[14px] leading-[1.7] text-[var(--yono-soft-black)]/85"
                    >
                      <span
                        className="mt-[10px] w-[6px] h-[6px] bg-[var(--yono-wood)] shrink-0"
                        aria-hidden
                      />
                      {c}
                    </li>
                  ))}
                </ul>

                <a
                  href="#contact"
                  className="btn-primary mt-10"
                  data-testid="produit-cta"
                  onClick={() => {
                    try { sessionStorage.removeItem("yono_form_type"); } catch (e) {}
                  }}
                >
                  Étudier ma configuration
                  <ArrowRight size={14} />
                </a>

                <div className="mt-10 pt-8 border-t border-[var(--yono-border)]" data-testid="produit-price-block">
                  <div className="flex items-baseline gap-3 mb-3">
                    <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-[var(--yono-warm-grey)]">
                      À partir de
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span
                      className="font-heading text-[36px] lg:text-[44px] leading-none text-[var(--yono-soft-black)]"
                      data-testid="produit-price"
                    >
                      19&nbsp;900&nbsp;€
                    </span>
                    <span className="font-mono text-[12px] tracking-[0.14em] uppercase text-[var(--yono-warm-grey)]">
                      HT
                    </span>
                  </div>
                  <p className="mt-3 text-[12px] text-[var(--yono-warm-grey)]">
                    Prix de vente du module YONO TWIN. Hors transport, options et installation.
                  </p>

                  <a
                    href="#contact"
                    className="btn-outline mt-6 w-full justify-center"
                    data-testid="produit-cta-location"
                    onClick={() => {
                      try { sessionStorage.setItem("yono_form_type", "Location"); } catch (e) {}
                      try {
                        window.dispatchEvent(
                          new CustomEvent("yono:prefill-type", { detail: { type: "Location" } })
                        );
                      } catch (e) {}
                    }}
                  >
                    Vous préférez louer&nbsp;? Demander un devis location
                    <ArrowRight size={14} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== USAGES ========== */}
      <section
        id="usages"
        className="section"
        data-testid="usages-section"
      >
        <div className="container-yono">
          <div className="eyebrow mb-6">Usages</div>
          <h2 className="font-heading text-[30px] sm:text-[36px] lg:text-[44px] leading-[1.1] tracking-tight max-w-3xl mb-14">
            Un logement adapté à plusieurs situations.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[var(--yono-border)] border border-[var(--yono-border)]">
            {USAGES.map((u) => (
              <div
                key={u.title}
                className="bg-[var(--yono-off-white)] p-10 lg:p-12 hover:bg-[var(--yono-beige-soft)] transition-colors duration-500"
                data-testid={`usage-card-${u.eyebrow}`}
              >
                <div className="flex items-start justify-between mb-8">
                  <u.icon
                    size={32}
                    strokeWidth={1.2}
                    className="text-[var(--yono-wood)]"
                  />
                  <span className="font-mono text-[11px] tracking-[0.18em] text-[var(--yono-wood)]">
                    {u.eyebrow}
                  </span>
                </div>
                <h3 className="font-heading text-[22px] lg:text-[26px] leading-tight mb-4">
                  {u.title}
                </h3>
                <p className="text-[14px] leading-[1.8] text-[var(--yono-warm-grey)]">
                  {u.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== AVANTAGES ========== */}
      <section
        id="avantages"
        className="section bg-[var(--yono-soft-black)] text-[var(--yono-off-white)]"
        data-testid="avantages-section"
      >
        <div className="container-yono">
          <div className="eyebrow text-[var(--yono-wood)] mb-6">Pourquoi YONO TWIN</div>
          <h2 className="font-heading text-[30px] sm:text-[36px] lg:text-[44px] leading-[1.1] tracking-tight max-w-3xl mb-14 text-white">
            Une réponse plus qualitative qu'une solution provisoire classique.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-12 gap-x-10">
            {AVANTAGES.map((a) => (
              <div
                key={a.n}
                className="border-t border-white/15 pt-6"
                data-testid={`avantage-${a.n}`}
              >
                <div className="font-mono text-[11px] tracking-[0.18em] text-[var(--yono-wood)] mb-4">
                  {a.n}
                </div>
                <h3 className="font-heading text-[22px] leading-tight mb-3 text-white">
                  {a.title}
                </h3>
                <p className="text-[14px] leading-[1.8] text-white/70">
                  {a.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== FABRICATION FRANÇAISE ========== */}
      <section
        className="section bg-[var(--yono-beige)]"
        data-testid="fabrication-section"
      >
        <div className="container-yono">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 order-2 lg:order-1">
              <div className="aspect-[4/3] overflow-hidden border border-[var(--yono-border)]">
                <img
                  src={ASSETS.atelier}
                  alt="Atelier YONO près de Nantes — fabrication de logements bois"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-6 order-1 lg:order-2">
              <div className="eyebrow mb-6">Fabrication française</div>
              <h2 className="font-heading text-[30px] sm:text-[36px] lg:text-[44px] leading-[1.1] tracking-tight">
                Un logement fabriqué en France, dans nos ateliers près de Nantes.
              </h2>
              <p className="mt-8 text-[15px] leading-[1.85] text-[var(--yono-warm-grey)] max-w-xl">
                Les logements YONO sont développés et fabriqués en atelier
                avec une approche centrée sur la qualité, la durabilité et la
                simplicité d'installation. La préfabrication permet de mieux
                maîtriser les délais, les finitions et la qualité du logement
                livré.
              </p>

              <div className="mt-10 grid grid-cols-2 gap-y-5 gap-x-6">
                {FABRICATION.map((f) => (
                  <div
                    key={f.label}
                    className="flex items-center gap-3"
                    data-testid={`fabrication-${f.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <f.icon
                      size={18}
                      className="text-[var(--yono-wood)] shrink-0"
                      strokeWidth={1.4}
                    />
                    <span className="text-[13px] text-[var(--yono-soft-black)]/85">
                      {f.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SHOWROOM ========== */}
      <section
        id="showroom"
        className="section"
        data-testid="showroom-section"
      >
        <div className="container-yono">
          <div className="border border-[var(--yono-border)] p-10 lg:p-20 text-center bg-white">
            <div className="eyebrow mb-6">Showroom</div>
            <h2 className="font-heading text-[28px] sm:text-[34px] lg:text-[44px] leading-[1.1] tracking-tight max-w-3xl mx-auto">
              Venez découvrir le logement au showroom.
            </h2>
            <p className="mt-6 max-w-2xl mx-auto text-[15px] leading-[1.85] text-[var(--yono-warm-grey)]">
              Un showroom est visible au sud de Nantes pour découvrir l'univers
              YONO, échanger sur votre besoin et étudier la configuration la
              plus adaptée à votre projet d'hébergement.
            </p>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto text-left">
              <div className="border border-[var(--yono-border)] p-6">
                <div className="flex items-start gap-3">
                  <MapPin size={18} className="text-[var(--yono-wood)] mt-1 shrink-0" strokeWidth={1.4} />
                  <div>
                    <div className="eyebrow mb-2">Adresse</div>
                    <p className="text-[14px] leading-[1.7] text-[var(--yono-soft-black)]/90">
                      9 rue Pasteur<br />
                      44310 Saint-Philbert-de-Grand-Lieu
                    </p>
                  </div>
                </div>
              </div>
              <div className="border border-[var(--yono-border)] p-6">
                <div className="flex items-start gap-3">
                  <Phone size={18} className="text-[var(--yono-wood)] mt-1 shrink-0" strokeWidth={1.4} />
                  <div>
                    <div className="eyebrow mb-2">Téléphone</div>
                    <a
                      href="tel:+33683581804"
                      className="text-[14px] leading-[1.7] text-[var(--yono-soft-black)] hover:text-[var(--yono-wood)]"
                    >
                      06 83 58 18 04
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <a
              href="#contact"
              className="btn-primary mt-10"
              data-testid="showroom-cta"
            >
              Prendre rendez-vous
              <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* ========== CONTACT ========== */}
      <section
        id="contact"
        className="section bg-[var(--yono-beige-soft)]"
        data-testid="contact-section"
      >
        <div className="container-yono">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="eyebrow mb-6">Demande d'étude</div>
              <h2 className="font-heading text-[28px] sm:text-[34px] lg:text-[42px] leading-[1.1] tracking-tight">
                Vous avez un besoin d'hébergement temporaire ou saisonnier&nbsp;?
              </h2>
              <p className="mt-8 text-[15px] leading-[1.85] text-[var(--yono-warm-grey)]">
                Expliquez-nous votre besoin&nbsp;: nombre de personnes à
                héberger, durée d'utilisation, site disponible, présence ou non
                de sanitaires communs, contraintes techniques et calendrier
                souhaité. Nous vous proposerons une configuration adaptée
                autour du YONO TWIN.
              </p>

              <div className="mt-10 space-y-5 text-[14px] text-[var(--yono-soft-black)]/85">
                <div className="flex items-center gap-3">
                  <MapPin size={16} className="text-[var(--yono-wood)]" strokeWidth={1.4} />
                  <span>9 rue Pasteur, 44310 Saint-Philbert-de-Grand-Lieu</span>
                </div>
                <div className="flex items-center gap-3">
                  <Phone size={16} className="text-[var(--yono-wood)]" strokeWidth={1.4} />
                  <a href="tel:+33683581804" className="hover:text-[var(--yono-wood)]">
                    06 83 58 18 04
                  </a>
                </div>
              </div>
            </div>
            <div className="lg:col-span-7">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <a
        href="#contact"
        className="sticky-cta hidden md:inline-flex"
        data-testid="sticky-cta"
      >
        Demander une étude
        <ArrowRight size={14} />
      </a>
    </div>
  );
}
